import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import GroupsIcon from "@mui/icons-material/Groups";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import SettingsInputComponentIcon from "@mui/icons-material/SettingsInputComponent";

const API_BASE =
  process.env.REACT_APP_API_BASE || "https://panel.haymestasarim.com/api";

const BRAND = {
  navy: "#0f172a",
  blue: "#2563eb",
  blueSoft: "#eff6ff",
  orange: "#f97316",
  orangeDark: "#c2410c",
  orangeSoft: "#fff7ed",
};

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("adminToken") ||
    ""
  );
}

async function api(path, options = {}) {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
      ...(options.headers || {}),
    },
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok || json?.ok === false) {
    throw new Error(json?.message || "İşlem tamamlanamadı");
  }
  return json;
}

const EMPTY_PERSON = {
  ad_soyad: "",
  pin: "",
  yetkili_salonlar: [],
  siparis_alabilir: true,
  mutfak_gorebilir: false,
  ozel_urun_ekleyebilir: false,
  odeme_gorebilir: false,
  aktif: true,
};

const EMPTY_STATION = {
  ad: "",
  kod: "",
  renk: "#2563eb",
  yazici_turu: "browser",
  yazici_adi: "",
  yazici_adresi: "",
  kagit_genisligi: 80,
  otomatik_yazdir: false,
  varsayilan: false,
  aktif: true,
};

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

export default function GarsonYonetimiPage() {
  const [activePanel, setActivePanel] = useState("people");
  const [data, setData] = useState({
    people: [],
    salons: [],
    stations: [],
    mappings: [],
    menu: [],
    stocks: [],
  });
  const [loading, setLoading] = useState(false);
  const [personDialog, setPersonDialog] = useState(false);
  const [stationDialog, setStationDialog] = useState(false);
  const [editingPersonId, setEditingPersonId] = useState(null);
  const [editingStationId, setEditingStationId] = useState(null);
  const [personForm, setPersonForm] = useState(EMPTY_PERSON);
  const [stationForm, setStationForm] = useState(EMPTY_STATION);
  const [mappingDraft, setMappingDraft] = useState({});
  const [productSearch, setProductSearch] = useState("");
  const [mappingSource, setMappingSource] = useState("menu");
  const [savingMappings, setSavingMappings] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });

  const notify = useCallback((message, severity = "success") => {
    setSnack({ open: true, severity, message });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const json = await api("/garson/admin/bootstrap?lang=tr");
      const next = json.data || {};
      setData({
        people: safeArray(next.people),
        salons: safeArray(next.salons),
        stations: safeArray(next.stations),
        mappings: safeArray(next.mappings),
        menu: safeArray(next.menu),
        stocks: safeArray(next.stocks),
      });
      const draft = {};
      safeArray(next.mappings).forEach((mapping) => {
        draft[`${mapping.urun_kaynagi}:${mapping.urun_id}`] = String(mapping.istasyon_id);
      });
      setMappingDraft(draft);
    } catch (error) {
      notify(error.message || "Garson yönetimi verileri alınamadı", "error");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  const openNewPerson = () => {
    setEditingPersonId(null);
    setPersonForm({ ...EMPTY_PERSON, yetkili_salonlar: [] });
    setPersonDialog(true);
  };

  const openEditPerson = (person) => {
    setEditingPersonId(person.id);
    setPersonForm({
      ad_soyad: person.ad_soyad || "",
      pin: "",
      yetkili_salonlar: safeArray(person.yetkili_salonlar).map(Number),
      siparis_alabilir: person.siparis_alabilir === true,
      mutfak_gorebilir: person.mutfak_gorebilir === true,
      ozel_urun_ekleyebilir: person.ozel_urun_ekleyebilir === true,
      odeme_gorebilir: person.odeme_gorebilir === true,
      aktif: person.aktif === true,
    });
    setPersonDialog(true);
  };

  const savePerson = async () => {
    if (!personForm.ad_soyad.trim()) {
      notify("Personel adı zorunludur", "warning");
      return;
    }
    if (!editingPersonId && !/^\d{4,6}$/.test(personForm.pin)) {
      notify("Yeni personel için 4–6 haneli PIN zorunludur", "warning");
      return;
    }
    if (personForm.pin && !/^\d{4,6}$/.test(personForm.pin)) {
      notify("PIN 4–6 haneli olmalıdır", "warning");
      return;
    }

    try {
      await api(
        editingPersonId
          ? `/garson/admin/personeller/${editingPersonId}`
          : "/garson/admin/personeller",
        {
          method: editingPersonId ? "PUT" : "POST",
          body: JSON.stringify(personForm),
        }
      );
      setPersonDialog(false);
      notify(editingPersonId ? "Personel güncellendi" : "Personel ve PIN oluşturuldu");
      await load();
    } catch (error) {
      notify(error.message, "error");
    }
  };

  const deactivatePerson = async (person) => {
    if (!window.confirm(`${person.ad_soyad} pasife alınsın mı? Geçmiş siparişleri silinmez.`)) {
      return;
    }
    try {
      await api(`/garson/admin/personeller/${person.id}`, { method: "DELETE" });
      notify("Personel pasife alındı");
      await load();
    } catch (error) {
      notify(error.message, "error");
    }
  };

  const openNewStation = () => {
    setEditingStationId(null);
    setStationForm({ ...EMPTY_STATION });
    setStationDialog(true);
  };

  const openEditStation = (station) => {
    setEditingStationId(station.id);
    setStationForm({
      ad: station.ad || "",
      kod: station.kod || "",
      renk: station.renk || "#2563eb",
      yazici_turu: station.yazici_turu || "browser",
      yazici_adi: station.yazici_adi || "",
      yazici_adresi: station.yazici_adresi || "",
      kagit_genisligi: Number(station.kagit_genisligi) === 58 ? 58 : 80,
      otomatik_yazdir: station.otomatik_yazdir === true,
      varsayilan: station.varsayilan === true,
      aktif: station.aktif === true,
    });
    setStationDialog(true);
  };

  const saveStation = async () => {
    if (!stationForm.ad.trim()) {
      notify("İstasyon adı zorunludur", "warning");
      return;
    }
    try {
      await api(
        editingStationId
          ? `/garson/admin/istasyonlar/${editingStationId}`
          : "/garson/admin/istasyonlar",
        {
          method: editingStationId ? "PUT" : "POST",
          body: JSON.stringify(stationForm),
        }
      );
      setStationDialog(false);
      notify(editingStationId ? "İstasyon güncellendi" : "İstasyon oluşturuldu");
      await load();
    } catch (error) {
      notify(error.message, "error");
    }
  };

  const deactivateStation = async (station) => {
    if (!window.confirm(`${station.ad} istasyonu pasife alınsın mı?`)) return;
    try {
      await api(`/garson/admin/istasyonlar/${station.id}`, { method: "DELETE" });
      notify("İstasyon pasife alındı");
      await load();
    } catch (error) {
      notify(error.message, "error");
    }
  };

  const menuProducts = useMemo(
    () =>
      data.menu.flatMap((section) =>
        safeArray(section.categories).flatMap((category) =>
          safeArray(category.items).map((item) => ({
            ...item,
            source: "menu",
            category_title: category.title,
            section_title: section.title,
          }))
        )
      ),
    [data.menu]
  );

  const visibleProducts = useMemo(() => {
    const source = mappingSource === "menu" ? menuProducts : data.stocks.map((item) => ({ ...item, source: "stock" }));
    const query = productSearch.trim().toLocaleLowerCase("tr-TR");
    return source.filter((item) =>
      query
        ? `${item.title || ""} ${item.category_title || ""} ${item.section_title || ""}`
            .toLocaleLowerCase("tr-TR")
            .includes(query)
        : true
    );
  }, [data.stocks, mappingSource, menuProducts, productSearch]);

  const saveMappings = async () => {
    setSavingMappings(true);
    try {
      const mappings = Object.entries(mappingDraft)
        .filter(([, stationId]) => Number(stationId) > 0)
        .map(([key, stationId]) => {
          const [urun_kaynagi, urunId] = key.split(":");
          return {
            urun_kaynagi,
            urun_id: Number(urunId),
            istasyon_id: Number(stationId),
          };
        });
      await api("/garson/admin/urun-istasyonlari", {
        method: "PUT",
        body: JSON.stringify({ mappings }),
      });
      notify("Tüm ürün-istasyon yönlendirmeleri kaydedildi");
      await load();
    } catch (error) {
      notify(error.message, "error");
    } finally {
      setSavingMappings(false);
    }
  };

  const panels = [
    { value: "people", label: "Personeller", icon: <GroupsIcon /> },
    { value: "stations", label: "İstasyon ve Yazıcılar", icon: <LocalPrintshopIcon /> },
    { value: "routing", label: "Ürün Yönlendirme", icon: <SettingsInputComponentIcon /> },
  ];

  return (
    <Box sx={{ minHeight: "100%", bgcolor: "#f8fafc", p: { xs: 1.3, md: 2.5 } }}>
      <Box sx={{ maxWidth: 1500, mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.7, md: 2.5 },
            mb: 2,
            borderRadius: 4,
            border: "1px solid #e2e8f0",
            background: "linear-gradient(135deg,#0f172a,#1e3a8a 62%,#f97316 150%)",
            color: "#fff",
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 950 }}>Garson ve Servis Yönetimi</Typography>
              <Typography sx={{ opacity: 0.78, fontWeight: 650 }}>
                Personel PIN’leri, salon yetkileri, hazırlama istasyonları ve yazıcı yönlendirmeleri
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
              onClick={load}
              disabled={loading}
              sx={{ bgcolor: BRAND.orange, fontWeight: 900, textTransform: "none", "&:hover": { bgcolor: BRAND.orangeDark } }}
            >
              Yenile
            </Button>
          </Stack>
        </Paper>

        <Box sx={{ overflowX: "auto", mb: 2 }}>
          <ToggleButtonGroup
            exclusive
            value={activePanel}
            onChange={(_event, value) => value && setActivePanel(value)}
            sx={{ minWidth: "max-content", bgcolor: "#fff" }}
          >
            {panels.map((panel) => (
              <ToggleButton key={panel.value} value={panel.value} sx={{ gap: 0.7, fontWeight: 900, textTransform: "none" }}>
                {panel.icon}{panel.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {activePanel === "people" && (
          <Stack spacing={1.5}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.navy }}>Garson ve Personeller</Typography>
                <Typography color="text.secondary">Her personel kendisine verilen 4–6 haneli PIN ile giriş yapar.</Typography>
              </Box>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openNewPerson} sx={{ bgcolor: BRAND.blue, fontWeight: 900, textTransform: "none" }}>
                Personel Ekle
              </Button>
            </Stack>

            {!data.people.length ? (
              <Alert severity="info">Henüz garson/personel oluşturulmadı.</Alert>
            ) : (
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2,minmax(0,1fr))", xl: "repeat(3,minmax(0,1fr))" }, gap: 1.3 }}>
                {data.people.map((person) => (
                  <Card key={person.id} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 3.5, opacity: person.aktif ? 1 : 0.62 }}>
                    <CardContent>
                      <Stack direction="row" spacing={1.2} alignItems="flex-start">
                        <Avatar sx={{ bgcolor: person.aktif ? BRAND.blue : "#94a3b8", fontWeight: 950 }}>
                          {String(person.ad_soyad || "P").slice(0, 1).toUpperCase()}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="h6" sx={{ fontWeight: 950 }} noWrap>{person.ad_soyad}</Typography>
                          <Stack direction="row" flexWrap="wrap" gap={0.6} sx={{ mt: 0.8 }}>
                            {person.siparis_alabilir && <Chip size="small" label="Sipariş" color="primary" />}
                            {person.mutfak_gorebilir && <Chip size="small" label="Mutfak" color="warning" />}
                            {person.ozel_urun_ekleyebilir && <Chip size="small" label="Özel Ürün" />}
                            {person.odeme_gorebilir && <Chip size="small" label="Ödeme" color="success" />}
                            {!person.aktif && <Chip size="small" label="Pasif" color="error" />}
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, fontWeight: 700 }}>
                            {safeArray(person.yetkili_salonlar).length
                              ? `${person.yetkili_salonlar.length} salon yetkisi`
                              : "Tüm salonlar"}
                          </Typography>
                        </Box>
                        <Stack direction="row">
                          <Tooltip title="Düzenle"><IconButton color="primary" onClick={() => openEditPerson(person)}><EditIcon /></IconButton></Tooltip>
                          {person.aktif && <Tooltip title="Pasife al"><IconButton color="error" onClick={() => deactivatePerson(person)}><DeleteIcon /></IconButton></Tooltip>}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Stack>
        )}

        {activePanel === "stations" && (
          <Stack spacing={1.5}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.navy }}>Hazırlama İstasyonları</Typography>
                <Typography color="text.secondary">Mutfak, kahve ve bar için Bluetooth, ağ veya tarayıcı yazıcısı tanımlayın.</Typography>
              </Box>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openNewStation} sx={{ bgcolor: BRAND.orange, fontWeight: 900, textTransform: "none" }}>
                İstasyon Ekle
              </Button>
            </Stack>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2,minmax(0,1fr))", xl: "repeat(3,minmax(0,1fr))" }, gap: 1.3 }}>
              {data.stations.map((station) => (
                <Card key={station.id} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 3.5, borderTop: `5px solid ${station.renk || BRAND.blue}`, opacity: station.aktif ? 1 : 0.62 }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 950 }}>{station.ad}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 750 }}>{station.kod}</Typography>
                      </Box>
                      <Stack direction="row">
                        <IconButton color="primary" onClick={() => openEditStation(station)}><EditIcon /></IconButton>
                        {station.aktif && <IconButton color="error" onClick={() => deactivateStation(station)}><DeleteIcon /></IconButton>}
                      </Stack>
                    </Stack>
                    <Divider sx={{ my: 1.2 }} />
                    <Stack direction="row" flexWrap="wrap" gap={0.7}>
                      <Chip label={station.yazici_turu === "bluetooth" ? "Bluetooth" : station.yazici_turu === "network" ? "Ağ Yazıcısı" : station.yazici_turu === "none" ? "Yazıcısız" : "Tarayıcı Yazdırma"} color={station.yazici_turu === "bluetooth" ? "primary" : "default"} />
                      <Chip label={`${station.kagit_genisligi || 80} mm`} />
                      {station.varsayilan && <Chip label="Varsayılan" color="warning" />}
                      {station.otomatik_yazdir && <Chip label="Otomatik" color="success" />}
                    </Stack>
                    {(station.yazici_adi || station.yazici_adresi) && (
                      <Typography variant="body2" sx={{ mt: 1.2, fontWeight: 700 }}>
                        {station.yazici_adi || station.yazici_adresi}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Stack>
        )}

        {activePanel === "routing" && (
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.navy }}>Ürün → İstasyon Yönlendirme</Typography>
              <Typography color="text.secondary">Her ürünü Mutfak, Kahve/Bar veya oluşturduğunuz başka bir istasyona yönlendirin.</Typography>
            </Box>

            <Paper elevation={0} sx={{ p: 1.5, border: "1px solid #e2e8f0", borderRadius: 3 }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <ToggleButtonGroup exclusive value={mappingSource} onChange={(_event, value) => value && setMappingSource(value)}>
                  <ToggleButton value="menu" sx={{ fontWeight: 900 }}>Menü Ürünleri</ToggleButton>
                  <ToggleButton value="stock" sx={{ fontWeight: 900 }}>Stok Ürünleri</ToggleButton>
                </ToggleButtonGroup>
                <TextField
                  value={productSearch}
                  onChange={(event) => setProductSearch(event.target.value)}
                  placeholder="Ürün ara"
                  size="small"
                  fullWidth
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                />
                <Button variant="contained" startIcon={savingMappings ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />} onClick={saveMappings} disabled={savingMappings} sx={{ minWidth: 170, bgcolor: BRAND.blue, fontWeight: 900, textTransform: "none" }}>
                  Tümünü Kaydet
                </Button>
              </Stack>
            </Paper>

            {!data.stations.filter((station) => station.aktif).length && (
              <Alert severity="warning">Ürün yönlendirmeden önce en az bir aktif istasyon oluşturun.</Alert>
            )}

            <Stack spacing={0.8}>
              {visibleProducts.map((product) => {
                const key = `${mappingSource}:${product.id}`;
                return (
                  <Paper key={key} elevation={0} sx={{ p: 1.2, border: "1px solid #e2e8f0", borderRadius: 2.5, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "minmax(0,1fr) 280px" }, gap: 1, alignItems: "center" }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 900 }} noWrap>{product.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{product.category_title || (mappingSource === "stock" ? `Stok: ${product.stock}` : "Menü")}</Typography>
                    </Box>
                    <TextField
                      select
                      size="small"
                      label="Hazırlama istasyonu"
                      value={mappingDraft[key] || ""}
                      onChange={(event) => setMappingDraft((old) => ({ ...old, [key]: event.target.value }))}
                    >
                      <MenuItem value=""><em>Varsayılan istasyon</em></MenuItem>
                      {data.stations.filter((station) => station.aktif).map((station) => (
                        <MenuItem key={station.id} value={String(station.id)}>{station.ad}</MenuItem>
                      ))}
                    </TextField>
                  </Paper>
                );
              })}
            </Stack>
          </Stack>
        )}
      </Box>

      <Dialog open={personDialog} onClose={() => setPersonDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 950 }}>{editingPersonId ? "Personeli Düzenle" : "Yeni Garson / Personel"}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.4} sx={{ mt: 1 }}>
            <TextField autoFocus label="Ad soyad" value={personForm.ad_soyad} onChange={(event) => setPersonForm((old) => ({ ...old, ad_soyad: event.target.value }))} />
            <TextField
              label={editingPersonId ? "Yeni PIN (değişmeyecekse boş bırakın)" : "4–6 haneli PIN"}
              type="password"
              value={personForm.pin}
              onChange={(event) => setPersonForm((old) => ({ ...old, pin: event.target.value.replace(/\D/g, "").slice(0, 6) }))}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            />
            <TextField
              select
              SelectProps={{ multiple: true }}
              label="Yetkili salonlar (boşsa tümü)"
              value={personForm.yetkili_salonlar}
              onChange={(event) => setPersonForm((old) => ({ ...old, yetkili_salonlar: event.target.value.map(Number) }))}
            >
              {data.salons.map((salon) => <MenuItem key={salon.id} value={salon.id}><Checkbox checked={personForm.yetkili_salonlar.includes(Number(salon.id))} />{salon.name}</MenuItem>)}
            </TextField>
            <Divider />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,1fr)" } }}>
              <FormControlLabel control={<Switch checked={personForm.siparis_alabilir} onChange={(event) => setPersonForm((old) => ({ ...old, siparis_alabilir: event.target.checked }))} />} label="Sipariş alabilir" />
              <FormControlLabel control={<Switch checked={personForm.mutfak_gorebilir} onChange={(event) => setPersonForm((old) => ({ ...old, mutfak_gorebilir: event.target.checked }))} />} label="Mutfak ekranı" />
              <FormControlLabel control={<Switch checked={personForm.ozel_urun_ekleyebilir} onChange={(event) => setPersonForm((old) => ({ ...old, ozel_urun_ekleyebilir: event.target.checked }))} />} label="Özel ürün ekler" />
              <FormControlLabel control={<Switch checked={personForm.odeme_gorebilir} onChange={(event) => setPersonForm((old) => ({ ...old, odeme_gorebilir: event.target.checked }))} />} label="Ödeme görür" />
              {editingPersonId && <FormControlLabel control={<Switch checked={personForm.aktif} onChange={(event) => setPersonForm((old) => ({ ...old, aktif: event.target.checked }))} />} label="Aktif" />}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPersonDialog(false)}>Vazgeç</Button>
          <Button variant="contained" onClick={savePerson}>Kaydet</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={stationDialog} onClose={() => setStationDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 950 }}>{editingStationId ? "İstasyonu Düzenle" : "Yeni Hazırlama İstasyonu"}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.4} sx={{ mt: 1 }}>
            <TextField label="İstasyon adı" placeholder="Mutfak, Kahve, Bar" value={stationForm.ad} onChange={(event) => setStationForm((old) => ({ ...old, ad: event.target.value }))} />
            {!editingStationId && <TextField label="İstasyon kodu (isteğe bağlı)" value={stationForm.kod} onChange={(event) => setStationForm((old) => ({ ...old, kod: event.target.value }))} />}
            <TextField select label="Yazıcı bağlantısı" value={stationForm.yazici_turu} onChange={(event) => setStationForm((old) => ({ ...old, yazici_turu: event.target.value }))}>
              <MenuItem value="bluetooth">Bluetooth yazıcı</MenuItem>
              <MenuItem value="network">Ağ / IP yazıcısı</MenuItem>
              <MenuItem value="browser">Tarayıcı / sistem yazdırma</MenuItem>
              <MenuItem value="none">Sadece mutfak ekranı</MenuItem>
            </TextField>
            <TextField label="Yazıcı adı / model" value={stationForm.yazici_adi} onChange={(event) => setStationForm((old) => ({ ...old, yazici_adi: event.target.value }))} />
            <TextField label="Bluetooth MAC veya ağ adresi" value={stationForm.yazici_adresi} onChange={(event) => setStationForm((old) => ({ ...old, yazici_adresi: event.target.value }))} />
            <TextField select label="Kâğıt genişliği" value={stationForm.kagit_genisligi} onChange={(event) => setStationForm((old) => ({ ...old, kagit_genisligi: Number(event.target.value) }))}>
              <MenuItem value={58}>58 mm</MenuItem>
              <MenuItem value={80}>80 mm</MenuItem>
            </TextField>
            <TextField select label="Renk" value={stationForm.renk} onChange={(event) => setStationForm((old) => ({ ...old, renk: event.target.value }))}>
              <MenuItem value="#2563eb">Mavi</MenuItem>
              <MenuItem value="#f97316">Turuncu</MenuItem>
              <MenuItem value="#16a34a">Yeşil</MenuItem>
              <MenuItem value="#7c3aed">Mor</MenuItem>
            </TextField>
            <FormControlLabel control={<Switch checked={stationForm.otomatik_yazdir} onChange={(event) => setStationForm((old) => ({ ...old, otomatik_yazdir: event.target.checked }))} />} label="Yazıcı köprüsü varsa otomatik yazdır" />
            <FormControlLabel control={<Switch checked={stationForm.varsayilan} onChange={(event) => setStationForm((old) => ({ ...old, varsayilan: event.target.checked }))} />} label="Yönlendirilmemiş ürünlerin varsayılan istasyonu" />
            {editingStationId && <FormControlLabel control={<Switch checked={stationForm.aktif} onChange={(event) => setStationForm((old) => ({ ...old, aktif: event.target.checked }))} />} label="Aktif" />}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStationDialog(false)}>Vazgeç</Button>
          <Button variant="contained" onClick={saveStation}>Kaydet</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack((old) => ({ ...old, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.severity} onClose={() => setSnack((old) => ({ ...old, open: false }))} sx={{ fontWeight: 850 }}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
